import { useEffect, useRef } from "react";
import * as THREE from "three";

const BLUE = 0x145ad3, RED = 0xd5272e, NAVY = 0x19314f;

const mat = (color, metalness = 0.2, roughness = 0.4) =>
  new THREE.MeshStandardMaterial({ color, metalness, roughness });
const emissive = (color, i = 1) =>
  new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: i });

function box(g, x, y, z, a, b, c, m) {
  const o = new THREE.Mesh(new THREE.BoxGeometry(a, b, c), m);
  o.position.set(x, y, z);
  o.castShadow = true;
  o.receiveShadow = true;
  g.add(o);
  return o;
}
function tube(g, pts, color, r = 0.025) {
  const path = new THREE.CatmullRomCurve3(pts.map((p) => new THREE.Vector3(...p)));
  g.add(new THREE.Mesh(new THREE.TubeGeometry(path, 70, r, 8, false), mat(color)));
  return path;
}
function pulseNode(g, r, color) {
  const o = new THREE.Mesh(new THREE.SphereGeometry(r, 16, 12), emissive(color, 1.4));
  g.add(o);
  return o;
}
function cyl(g, x, y, z, r, h, color) {
  const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r, h, 32), mat(color, 0.7, 0.25));
  m.position.set(x, y, z);
  m.castShadow = true;
  g.add(m);
  return m;
}
function ring(g, x, y, z, r, thickness, color) {
  const m = new THREE.Mesh(new THREE.TorusGeometry(r, thickness, 12, 48), mat(color, 0.6, 0.25));
  m.position.set(x, y, z);
  g.add(m);
  return m;
}
function glove(g, color = 0x2871bb) {
  const hand = new THREE.Group();
  box(hand, 0, 0, 0, 0.42, 0.15, 0.46, mat(color, 0, 0.75));
  for (let j = 0; j < 4; j++) box(hand, (j - 1.5) * 0.105, 0, 0.3, 0.075, 0.13, 0.28, mat(color, 0, 0.7));
  box(hand, -0.23, 0, 0.03, 0.14, 0.13, 0.25, mat(color, 0, 0.7));
  const sleeve = cyl(hand, 0, 0, -0.48, 0.2, 0.65, NAVY);
  sleeve.rotation.x = Math.PI / 2;
  g.add(hand);
  return hand;
}
function hardware(g, x, y, z) {
  cyl(g, x, y, z, 0.05, 0.07, 0xcad6e2);
  box(g, x, y + 0.042, z, 0.055, 0.012, 0.013, mat(0x8ea2bd));
}

function buildTruck(g, paintHex = BLUE) {
  const car = new THREE.Group();
  g.add(car);
  const paint = mat(paintHex, 0.55, 0.23);
  const glass = mat(0x294566, 0.7, 0.15);
  const dark = mat(NAVY, 0.3, 0.5);
  box(car, 0, 0.7, 0, 3.9, 0.56, 1.7, paint);
  box(car, 0.2, 1.15, 0, 1.8, 0.62, 1.52, glass);
  box(car, 0.2, 1.5, 0, 1.75, 0.09, 1.55, paint);
  box(car, 1.4, 1.02, 0, 1.12, 0.12, 1.64, paint);
  box(car, -1.55, 1.0, 0, 0.8, 0.12, 1.6, paint);
  box(car, 1.99, 0.69, 0, 0.06, 0.19, 1.05, dark);
  box(car, -2.0, 0.65, 0, 0.05, 0.18, 1.15, dark);
  const lights = [];
  [-0.58, 0.58].forEach((z) => {
    const m = new THREE.MeshStandardMaterial({ color: 0xdaf4ff, emissive: 0x87c9ff, emissiveIntensity: 0.6 });
    lights.push(m);
    box(car, 1.97, 0.86, z, 0.06, 0.15, 0.28, m);
    box(car, -1.98, 0.85, z, 0.06, 0.13, 0.28, mat(RED));
  });
  [-1.23, 1.25].forEach((x) => [-0.89, 0.89].forEach((z) => {
    const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.44, 0.44, 0.23, 32), mat(0x182231, 0, 0.8));
    wheel.rotation.x = Math.PI / 2;
    wheel.position.set(x, 0.43, z);
    car.add(wheel);
    const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.25, 0.25, 0.245, 20), mat(0xd6e0ed, 0.8, 0.18));
    hub.rotation.x = Math.PI / 2;
    hub.position.copy(wheel.position);
    car.add(hub);
  }));
  return { car, lights, paint };
}

function build(i, model) {
  const pulses = [], effects = [], work = {};
  let scanner = null, energy = null, car = null, lights = null, paint = null;
  if (i === 0) {
    ({ car, lights, paint } = buildTruck(model));
    paint.transparent = true;
    paint.opacity = 0.64;
    const routes = [
      [[1.25, 0.99, -0.3], [0.7, 1, -0.3], [-0.2, 1, -0.55], [-1.3, 0.96, -0.55], [-1.95, 0.85, -0.55]],
      [[1.25, 0.99, 0.3], [0.5, 0.99, 0.3], [-0.2, 1, 0.55], [-1.3, 0.96, 0.55], [-1.95, 0.85, 0.55]],
      [[1.3, 0.98, 0], [1.5, 1.0, 0.55], [1.85, 0.94, 0.55]],
      [[1.3, 0.98, 0], [1.5, 1, -0.55], [1.85, 0.94, -0.55]],
    ];
    routes.forEach((pts, j) => {
      const path = tube(model, pts, j < 2 ? RED : BLUE, 0.035);
      for (let n = 0; n < 3; n++) {
        const node = pulseNode(model, 0.072, j < 2 ? RED : BLUE);
        pulses.push({ path, node, offset: n / 3 });
      }
    });
    for (let j = 0; j < 10; j++) tube(model, [[-2.8, 0, j * 0.5 - 2.3], [-2.3, 0, j * 0.5 - 2.3], [-1.9, 0, j * 0.5 - 2], [2.4, 0, j * 0.5 - 2]], 0xaec5e9, 0.009);
    return { pulses, effects, work, car };
  }
  ({ car, lights, paint } = buildTruck(model, i === 1 ? 0xe3ecf8 : BLUE));
  if (i === 1) {
    scanner = new THREE.Mesh(new THREE.PlaneGeometry(3.1, 2.4),
      new THREE.MeshBasicMaterial({ color: BLUE, transparent: true, opacity: 0.12, side: THREE.DoubleSide, depthWrite: false }));
    scanner.rotation.y = Math.PI / 2;
    scanner.position.y = 1;
    model.add(scanner);
    const border = new THREE.LineSegments(new THREE.EdgesGeometry(scanner.geometry),
      new THREE.LineBasicMaterial({ color: 0x145ad3, transparent: true, opacity: 0.6 }));
    scanner.add(border);
    [[1.3, 1.12, 0.3], [0.3, 1.62, 0], [-1.2, 0.92, -0.4]].forEach((p, j) => {
      const r = new THREE.Mesh(new THREE.TorusGeometry(0.32, 0.018, 8, 40),
        new THREE.MeshBasicMaterial({ color: j === 0 ? RED : BLUE }));
      r.rotation.x = -Math.PI / 2;
      r.position.set(...p);
      model.add(r);
      effects.push(r);
    });
  } else if (i === 2) {
    const battery = new THREE.Group();
    battery.position.set(1.15, 0.5, 2.05);
    battery.rotation.y = -0.2;
    model.add(battery);
    box(battery, 0, 0.4, 0, 1.05, 0.8, 0.58, mat(0xf7faff, 0.25, 0.32));
    box(battery, 0, 0.85, 0, 1.1, 0.14, 0.63, mat(NAVY));
    box(battery, -0.3, 0.99, 0, 0.15, 0.13, 0.18, mat(RED));
    box(battery, 0.3, 0.99, 0, 0.15, 0.13, 0.18, mat(BLUE));
    const p1 = tube(model, [[0.85, 1.49, 2.05], [0.5, 1.2, 1.55], [1.3, 1.1, 0.6], [1.65, 0.99, 0.3]], RED, 0.035);
    const p2 = tube(model, [[1.45, 1.49, 2.05], [2.4, 0.8, 1.6], [1.8, 0.98, -0.3]], BLUE, 0.035);
    pulses.push({ path: p1, node: pulseNode(model, 0.07, RED), offset: 0 }, { path: p2, node: pulseNode(model, 0.07, BLUE), offset: 0.4 });
    energy = box(battery, -0.35, 0.4, 0.304, 0.72, 0.32, 0.025, new THREE.MeshStandardMaterial({ color: BLUE, emissive: BLUE, emissiveIntensity: 0.5 }));
    energy.geometry.translate(0.36, 0, 0);
  } else {
    car.visible = false;
    const platform = box(model, 0, 0.18, 0, 4.8, 0.25, 3.4, mat(NAVY, 0.4, 0.4));
    if (i === 3) {
      box(model, -0.6, 0.7, -0.15, 1.8, 0.78, 1.6, mat(0xb7c4d3, 0.65, 0.35));
      for (let j = 0; j < 6; j++) { box(model, -1.4 + j * 0.3, 1.12, -0.15, 0.13, 0.08, 1.55, mat(0x8ea2bd)); hardware(model, -1.4 + j * 0.3, 1.19, 0.48); }
      box(model, -0.6, 1.22, -0.15, 1.95, 0.18, 0.72, mat(BLUE, 0.55, 0.3));
      for (let j = 0; j < 4; j++) { const loop = ring(model, -1.2 + j * 0.4, 0.65, 0.73, 0.19, 0.035, 0x667789); loop.rotation.y = Math.PI / 2; }
      const batt = new THREE.Group();
      batt.position.set(1.18, 0.4, -0.65);
      model.add(batt);
      box(batt, 0, 0.38, 0, 1.1, 0.76, 0.8, mat(0x263347));
      box(batt, 0, 0.83, 0, 1.15, 0.15, 0.88, mat(0x42516a));
      [-0.32, 0.32].forEach((x, j) => { cyl(batt, x, 0.98, 0, 0.1, 0.18, j ? BLUE : RED); box(batt, x, 1.1, 0, 0.22, 0.12, 0.25, mat(j ? BLUE : RED)); });
      for (let j = 0; j < 6; j++) box(batt, -0.42 + j * 0.17, 0.96, -0.24, 0.12, 0.03, 0.1, mat(0x8496ae));
      const meter = new THREE.Group();
      meter.position.set(0.9, 0.55, 1.03);
      meter.rotation.x = -0.2;
      model.add(meter);
      box(meter, 0, 0.3, 0, 0.88, 0.6, 0.32, mat(RED));
      box(meter, 0, 0.43, 0.17, 0.65, 0.24, 0.018, mat(0x0d233f));
      work.bars = [];
      for (let j = 0; j < 7; j++) work.bars.push(box(meter, -0.26 + j * 0.085, 0.43, 0.19, 0.05, 0.06, 0.02, new THREE.MeshStandardMaterial({ color: 0x75c5ef, emissive: BLUE, emissiveIntensity: 0.7 })));
      const dial = cyl(meter, 0, 0.12, 0.22, 0.12, 0.04, NAVY);
      dial.rotation.x = Math.PI / 2;
      for (let j = 0; j < 4; j++) box(meter, -0.25 + j * 0.17, 0.62, 0.17, 0.1, 0.04, 0.02, mat(0x8798b1));
      [[RED, -0.32], [BLUE, 0.32]].forEach(([col, x], j) => {
        const path = tube(model, [[0.7 + j * 0.38, 0.43, 1.2], [2.1, 0.5, 1.4], [2.25, 0.65, 0.1], [1.18 + x, 1.43, -0.65]], col, 0.024);
        pulses.push({ path, node: pulseNode(model, 0.05, col), offset: j * 0.5 });
      });
      work.probe = new THREE.Group();
      model.add(work.probe);
      box(work.probe, 0, 0, 0, 0.12, 0.14, 0.65, mat(RED));
      box(work.probe, 0, 0, 0.44, 0.025, 0.04, 0.28, mat(0xcbd5df));
      work.probe.position.set(0.86, 1.52, -0.2);
      work.probe.rotation.x = -0.6;
      work.hand = glove(model);
      work.hand.position.set(0.86, 1.52, -0.65);
      work.hand.rotation.x = -0.6;
      const alt = cyl(model, -1.55, 0.56, -1.05, 0.3, 0.48, 0xa5b5c9);
      alt.rotation.z = Math.PI / 2;
      work.rotor = ring(model, -1.81, 0.56, -1.05, 0.24, 0.05, NAVY);
      work.rotor.rotation.y = Math.PI / 2;
      tube(model, [[-1.7, 0.6, -1.03], [-1.6, 0.95, -1.3], [-1.1, 1.07, -1.2]], NAVY, 0.05);
    } else if (i === 4) {
      box(model, -1.5, 0.5, -0.6, 0.9, 0.35, 1.2, mat(0xa3b5cc));
      for (let j = 0; j < 8; j++) box(model, -1.82 + j * 0.09, 0.7, -0.6, 0.04, 0.045, 0.9, mat(0x8ea2bd));
      const colors = [RED, BLUE, 0xe0ac36, 0xd8e5ef, 0x55746a, 0x8ea2bd];
      colors.forEach((col, j) => {
        const path = tube(model, [[-1.65 + j * 0.1, 0.7, -0.7], [-0.7, 0.65, -0.7 + j * 0.08], [-0.1, 0.5, -0.45 + j * 0.13], [0.5, 0.56, -0.45 + j * 0.13]], col, 0.026);
        pulses.push({ path, node: pulseNode(model, 0.05, col), offset: j / 6 });
      });
      const connector = new THREE.Group();
      connector.position.set(0.5, 0.58, -0.1);
      model.add(connector);
      box(connector, 0, 0, 0, 0.46, 0.43, 1.1, mat(0x354864));
      box(connector, 0.25, 0, 0, 0.1, 0.28, 0.94, mat(0x8c9eaf));
      for (let j = 0; j < 6; j++) { const pin = cyl(connector, 0.35, 0, -0.4 + j * 0.16, 0.025, 0.3, 0xd9b467); pin.rotation.z = Math.PI / 2; }
      work.plug = new THREE.Group();
      work.plug.position.set(1.45, 0.58, -0.1);
      model.add(work.plug);
      box(work.plug, 0, 0, 0, 0.54, 0.46, 1.1, mat(BLUE));
      box(work.plug, 0, 0.27, 0, 0.35, 0.08, 0.25, mat(NAVY));
      colors.forEach((col, j) => tube(work.plug, [[0.3, 0, -0.4 + j * 0.16], [0.7, -0.1, -0.4 + j * 0.16], [1.1, -0.2, -0.65 + j * 0.25]], col, 0.025));
      work.hand = glove(model);
      work.hand.position.set(1.4, 0.9, 0.4);
      work.hand.rotation.y = Math.PI / 2;
      work.tool = new THREE.Group();
      model.add(work.tool);
      work.tool.position.set(-0.1, 0.78, 1.12);
      work.tool.rotation.y = -0.2;
      box(work.tool, -0.1, 0, -0.05, 0.11, 0.16, 0.75, mat(0xbdc8d3));
      box(work.tool, 0.1, 0, -0.05, 0.11, 0.16, 0.75, mat(0xbdc8d3));
      box(work.tool, -0.13, 0, 0.5, 0.14, 0.2, 0.6, mat(RED));
      box(work.tool, 0.13, 0, 0.5, 0.14, 0.2, 0.6, mat(RED));
      hardware(work.tool, 0, 0.1, 0.05);
      for (let j = 0; j < 5; j++) box(model, -1.7 + j * 0.2, 0.42, 1.05, 0.1, 0.08, 0.25, mat(0xe0b168));
      work.probe = new THREE.Group();
      model.add(work.probe);
      box(work.probe, 0, 0, 0, 0.1, 0.13, 0.6, mat(BLUE));
      box(work.probe, 0, 0, 0.43, 0.025, 0.025, 0.26, mat(0xd1d8df));
      work.probe.position.set(0.58, 1.1, 0.5);
      work.probe.rotation.x = -0.7;
      cyl(model, -1.6, 0.6, 1.35, 0.25, 0.18, 0xc3cad4);
      const spool = ring(model, -1.6, 0.75, 1.35, 0.14, 0.04, NAVY);
      spool.rotation.x = Math.PI / 2;
    } else {
      const parts = new THREE.Group();
      model.add(parts);
      parts.rotation.x = Math.PI / 2;
      parts.rotation.z = Math.PI / 2;
      work.parts = [];
      const end = cyl(parts, 0, 0, 0, 0.68, 0.25, 0xaebdce);
      work.parts.push({ node: end, base: -1.65 });
      const windings = cyl(parts, 0, 0, 0, 0.6, 0.6, 0xb37b41);
      work.parts.push({ node: windings, base: -0.8 });
      for (let j = 0; j < 16; j++) {
        const angle = (j * Math.PI) / 8;
        cyl(windings, Math.cos(angle) * 0.53, 0, Math.sin(angle) * 0.53, 0.048, 0.62, 0xda9453);
      }
      const rotor = new THREE.Group();
      parts.add(rotor);
      cyl(rotor, 0, 0, 0, 0.09, 1.35, 0xcbd6e2);
      cyl(rotor, 0, 0, 0, 0.37, 0.62, 0x526a83);
      for (let j = 0; j < 12; j++) {
        const angle = (j * Math.PI) / 6;
        box(rotor, Math.cos(angle) * 0.34, 0, Math.sin(angle) * 0.34, 0.1, 0.67, 0.1, mat(0xb9c4d1));
      }
      work.parts.push({ node: rotor, base: 0.25 });
      work.rotor = rotor;
      const housing = cyl(parts, 0, 0, 0, 0.69, 0.38, 0xb8c7d8);
      for (let j = 0; j < 10; j++) {
        const angle = (j * Math.PI) / 5;
        box(housing, Math.cos(angle) * 0.67, 0, Math.sin(angle) * 0.67, 0.07, 0.33, 0.07, mat(0x8ea2bd));
      }
      work.parts.push({ node: housing, base: 1.2 });
      const pulley = cyl(parts, 0, 0, 0, 0.45, 0.2, NAVY);
      work.parts.push({ node: pulley, base: 1.95 });
      work.parts.forEach((p) => (p.node.position.y = p.base));
      parts.position.y = 1.3;
      work.tool = new THREE.Group();
      model.add(work.tool);
      box(work.tool, 0, 0, 0, 0.12, 0.12, 1.35, mat(0xc4d3e2));
      const wrench = ring(work.tool, 0, 0, 0.78, 0.16, 0.05, 0xcbd6e2);
      wrench.rotation.x = Math.PI / 2;
      work.tool.position.set(1.7, 0.75, 1.15);
      work.hand = glove(model);
      work.hand.position.set(1.7, 0.85, 1.72);
      work.hand.rotation.y = 0.2;
      for (let j = 0; j < 6; j++) hardware(model, -1.6 + j * 0.22, 0.36, 1.2);
      box(model, -1.4, 0.56, -1.2, 0.8, 0.32, 0.48, mat(NAVY));
      for (let j = 0; j < 3; j++) box(model, -1.68 + j * 0.25, 0.75, -1.2, 0.15, 0.18, 0.16, mat(0xd69a57));
    }
  }
  return { pulses, effects, work, scanner, energy, lights, car };
}

export default function Scene3D({ variant = 0, className = "", hint = "Drag to rotate", paused = false }) {
  const wrap = useRef(null);
  const stateRef = useRef(null);

  useEffect(() => {
    const stage = wrap.current;
    if (!stage) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    stage.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(36, 1, 0.1, 100);
    camera.position.set(7, 5, 7);
    camera.lookAt(0, 0.7, 0);
    scene.add(new THREE.HemisphereLight(0xffffff, 0x8494ac, 2.8));
    const light = new THREE.DirectionalLight(0xffffff, 4);
    light.position.set(4, 8, 5);
    light.castShadow = true;
    light.shadow.mapSize.set(1024, 1024);
    scene.add(light);

    const model = new THREE.Group();
    scene.add(model);
    model.rotation.y = -0.25;

    const floor = new THREE.Mesh(new THREE.CylinderGeometry(3.7, 3.7, 0.14, 80), mat(0xe1eaf6, 0, 0.8));
    floor.position.y = -0.12;
    floor.receiveShadow = true;
    model.add(floor);

    const built = build(variant, model);

    let dragging = false, lastX = 0;
    const canvas = renderer.domElement;
    canvas.addEventListener("pointerdown", (e) => { dragging = true; lastX = e.clientX; canvas.setPointerCapture(e.pointerId); });
    canvas.addEventListener("pointermove", (e) => { if (dragging) { model.rotation.y += (e.clientX - lastX) * 0.008; lastX = e.clientX; } });
    canvas.addEventListener("pointerup", () => (dragging = false));
    canvas.addEventListener("pointercancel", () => (dragging = false));

    const state = { renderer, scene, camera, model, ...built, t: 0, paused: reduced, reduced };
    stateRef.current = state;

    const resize = () => {
      const w = stage.clientWidth, h = stage.clientHeight;
      if (!w || !h) return;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      const fit = Math.max(1, 1.5 / camera.aspect);
      camera.position.set(7 * fit, 5 * fit, 7 * fit);
      camera.lookAt(0, 0.7, 0);
      camera.updateProjectionMatrix();
      renderer.render(scene, camera);
    };
    const ro = new ResizeObserver(resize);
    ro.observe(stage);
    resize();

    let last = performance.now();
    let raf;
    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      if ((state.paused || stage.offsetParent === null) && variant < 3) { last = now; return; }
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      state.t += dt;
      const t = state.t;
      state.pulses.forEach((p) => {
        const u = ((t * 0.22 + p.offset) % 1 + 1) % 1;
        if (!isFinite(u)) return;
        const pt = p.path.getPointAt(u);
        if (pt && !isNaN(pt.x)) p.node.position.copy(pt);
      });
      if (state.scanner) state.scanner.position.x = Math.sin(t * 0.8) * 2.3;
      state.effects.forEach((e, j) => e.scale.setScalar(1 + 0.2 * Math.sin(t * 2 + j)));
      if (state.energy) {
        state.energy.scale.x = 0.15 + 0.85 * ((Math.sin(t * 0.85) + 1) / 2);
        state.lights.forEach((m) => (m.emissiveIntensity = 0.4 + 1.5 * ((Math.sin(t * 0.85) + 1) / 2)));
      }
      if (variant < 3 && state.car) state.car.position.y = 0.012 * Math.sin(t * 1.7);
      const w = state.work;
      if (variant === 3) {
        w.probe.position.z = -0.2 + 0.16 * Math.sin(t);
        w.hand.position.z = -0.65 + 0.16 * Math.sin(t);
        w.rotor.rotation.z = t;
        w.bars.forEach((b, j) => (b.scale.y = 0.6 + 1.3 * Math.abs(Math.sin(t * 2 - j * 0.35))));
      }
      if (variant === 4) {
        const movement = 0.35 + 0.25 * Math.sin(t * 0.9);
        w.plug.position.x = 1.1 + movement;
        w.hand.position.x = 1.05 + movement;
        w.probe.position.y = 1 + 0.16 * Math.sin(t * 1.2);
        w.tool.rotation.z = 0.08 * Math.sin(t * 2.3);
      }
      if (variant === 5) {
        w.parts.forEach((p) => (p.node.position.y = p.base * (1 + 0.14 * Math.sin(t * 0.7))));
        w.rotor.rotation.y = t * 0.75;
        w.tool.rotation.y = 0.3 * Math.sin(t);
        w.hand.rotation.y = 0.2 + 0.15 * Math.sin(t);
      }
      renderer.render(scene, camera);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      renderer.dispose();
      stage.removeChild(canvas);
    };
  }, [variant]);

  useEffect(() => {
    const s = stateRef.current;
    if (s) s.paused = paused || s.reduced;
  }, [paused]);

  return (
    <div ref={wrap} className={`scene-stage ${className}`} data-testid={`scene3d-variant-${variant}`} />
  );
}
